import React, { useMemo } from 'react';
import ManCrossPageFooter from '../components/ManCrossPageFooter';
import {
    computeIvcLevel,
    formatRounded,
    toDimensionRows,
    getStatusTone,
} from '../shared/managerialUtils';

// ── Tier config ────────────────────────────────────────────────────────────────
const TIER = {
    critical: {
        circle:    '#F3202A',
        badgeBg:   '#F3202A',
        badgeText: '#fff',
        label:     'RIESGO CRÍTICO',
    },
    moderate: {
        circle:    '#D4AC0D',
        badgeBg:   '#D4AC0D',
        badgeText: '#1a1a1a',
        label:     'RIESGO MODERADO',
    },
    low: {
        circle:    '#62C190',
        badgeBg:   '#62C190',
        badgeText: '#fff',
        label:     'RIESGO BAJO',
    },
};

// ── Narrative builders ─────────────────────────────────────────────────────────

/** Critical: IVC area risk concentration + supervisor Pareto */
function buildCriticalFindings(areas, criticalSups, headcountMap, ipraTotals) {
    const findings = [];

    // H: Top IVC area and headcount share
    const ivcAreas = areas
        .filter(a => a.ivcLevel > 0)
        .sort((a, b) => b.ivcLevel - a.ivcLevel || (a.icp ?? 999) - (b.icp ?? 999));

    if (ivcAreas.length > 0) {
        const top      = ivcAreas[0];
        const totalHC  = Object.values(headcountMap).reduce((s, v) => s + v, 0);
        const areaHC   = headcountMap[top.name] ?? 0;
        const pct      = totalHC > 0 ? Math.round(areaHC / totalHC * 100) : null;
        findings.push(
            pct != null
                ? `${top.name} concentra el ${pct}% del personal expuesto con IVC Nivel ${top.ivcLevel} activo.`
                : `${top.name} presenta IVC Nivel ${top.ivcLevel} — la condición de mayor emergencia del sistema.`
        );
    } else {
        // Fallback: area with lowest ICP
        const worst = [...areas].sort((a, b) => (a.icp ?? 999) - (b.icp ?? 999))[0];
        if (worst) {
            findings.push(`${worst.name} registra el ICP más bajo de la planta (${formatRounded(worst.icp)}) — monitoreo prioritario.`);
        }
    }

    // H: Supervisor Pareto
    if (criticalSups && criticalSups.length > 0) {
        const n     = criticalSups.length;
        const total = ipraTotals?.relationships_finished ?? 0;
        const pct   = total > 0 ? Math.round(n / total * 100) : null;
        const avgScore = Math.round(
            criticalSups.reduce((s, c) => s + (c.ipra_score ?? 0), 0) / n
        );
        findings.push(
            pct != null
                ? `El ${pct}% del equipo directivo (${n} supervisor${n > 1 ? 'es' : ''}) concentra el mayor déficit de liderazgo preventivo — IPRA promedio ${avgScore}.`
                : `${n} supervisor${n > 1 ? 'es' : ''} con IPRA < 75 — déficit crítico de liderazgo preventivo (IPRA promedio ${avgScore}).`
        );
    }

    return findings;
}

/** Moderate: Co-failure dimension deficits + NPS alert */
function buildModerateFindings(iashDimRows, ipraDimRows, nps) {
    const findings = [];

    // H: Bottom 2 IASH dimensions below 65
    const bottomIash = iashDimRows
        .filter(r => r.average != null && r.average < 65)
        .sort((a, b) => a.average - b.average)
        .slice(0, 2);

    if (bottomIash.length >= 2) {
        const [d1, d2] = bottomIash;
        const mult = Math.max(1.5, Math.round(((65 - d1.average) / Math.max(1, 65 - (d1.average + d2.average) / 2)) * 20) / 10);
        findings.push(
            `Déficit en ${d1.dimension} (${formatRounded(d1.average)}) y ${d2.dimension} (${formatRounded(d2.average)}) — las dos brechas de mayor peso en el autocuidado colectivo.`
        );
    } else if (bottomIash.length === 1) {
        const [d1] = bottomIash;
        findings.push(`Brecha prioritaria en ${d1.dimension} (${formatRounded(d1.average)}) — dimensión de autocuidado más crítica.`);
    } else {
        // Use bottom overall dims
        const bottomAll = [...iashDimRows]
            .filter(r => r.average != null)
            .sort((a, b) => a.average - b.average)
            .slice(0, 2);
        if (bottomAll.length >= 2) {
            findings.push(
                `Las dimensiones con menor desempeño son ${bottomAll[0].dimension} (${formatRounded(bottomAll[0].average)}) y ${bottomAll[1].dimension} (${formatRounded(bottomAll[1].average)}).`
            );
        }
    }

    // H: NPS predictive signal (only if NPS is defined)
    const npsVal = typeof nps === 'number' ? Math.round(nps) : null;
    if (npsVal !== null) {
        if (npsVal < 0) {
            findings.push(`La desconfianza (NPS ${npsVal}) antecede y predice el deterioro de las barreras conductuales de seguridad.`);
        } else if (npsVal < 20) {
            findings.push(`NPS de seguridad en ${npsVal} — señal de alerta temprana en la percepción del entorno preventivo.`);
        } else {
            findings.push(`NPS de seguridad positivo (${npsVal > 0 ? '+' : ''}${npsVal}) — percepción favorable del entorno preventivo.`);
        }
    }

    return findings;
}

/** Low (green): Strongest dimensions as positive assets */
function buildLowFindings(iashDimRows, ipraDimRows) {
    const findings = [];

    // Top 2 scoring IASH dimensions above 70
    const topIash = iashDimRows
        .filter(r => r.average != null && r.average >= 70)
        .sort((a, b) => b.average - a.average)
        .slice(0, 2);

    if (topIash.length >= 2) {
        const [d1, d2] = topIash;
        findings.push(
            `${d1.dimension} (${formatRounded(d1.average)}) y ${d2.dimension} (${formatRounded(d2.average)}) son los activos de mayor fortaleza para el refuerzo preventivo.`
        );
    } else if (topIash.length === 1) {
        const [d1] = topIash;
        findings.push(`${d1.dimension} (${formatRounded(d1.average)}) destaca como principal activo de autocuidado colectivo.`);
    } else {
        // Fallback: just report the highest dimensions regardless of threshold
        const best = [...iashDimRows]
            .filter(r => r.average != null)
            .sort((a, b) => b.average - a.average)
            .slice(0, 2);
        if (best.length >= 2) {
            findings.push(
                `Las dimensiones de mayor fortaleza son ${best[0].dimension} (${formatRounded(best[0].average)}) y ${best[1].dimension} (${formatRounded(best[1].average)}).`
            );
        }
    }

    // Top IPRA dimension as leadership asset
    const topIpra = ipraDimRows
        .filter(r => r.average != null && r.average >= 75)
        .sort((a, b) => b.average - a.average)[0];

    if (topIpra) {
        findings.push(`${topIpra.dimension} (${formatRounded(topIpra.average)}) es la dimensión de liderazgo con mayor margen positivo — base para la réplica a otras áreas.`);
    }

    return findings;
}

// ── FindingsSection ────────────────────────────────────────────────────────────
const FindingsSection = ({ tierKey, sectionTitle, findings, startIndex }) => {
    const t = TIER[tierKey];
    return (
        <div className="mgcr-kf-section">
            {/* Left: colored circle */}
            <span className="mgcr-kf-circle" style={{ background: t.circle }} />

            {/* Right: content */}
            <div className="mgcr-kf-content">
                {/* Badge */}
                <span
                    className="mgcr-kf-badge"
                    style={{ background: t.badgeBg, color: t.badgeText }}
                >
                    {t.label}
                </span>

                {/* Optional section subtitle */}
                {sectionTitle && (
                    <p className="mgcr-kf-section-title">{sectionTitle}</p>
                )}

                {/* Findings */}
                <div className="mgcr-kf-findings">
                    {findings.map((text, i) => {
                        const num = String(startIndex + i).padStart(2, '0');
                        return (
                            <p key={i} className="mgcr-kf-finding">
                                <span className="mgcr-kf-finding-label">Hallazgo {num}:</span>
                                {' '}{text}
                            </p>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

// ── Main component ─────────────────────────────────────────────────────────────
const ManCrossPageKeyFindings = ({ reportData, headerText, pageNumber }) => {
    const payload     = reportData?.payload || {};
    const kpis        = payload?.kpis       || {};
    const iashTotals  = payload?.totals?.iash || {};
    const ipraTotals  = payload?.totals?.ipra || {};
    const criticalSups = payload?.critical_supervisors || [];

    const headcountMap = useMemo(() => {
        const map = {};
        (payload?.headcount_by_management_area || []).forEach(e => { map[e.name] = e.headcount; });
        return map;
    }, [payload]);

    // Per-area IVC data
    const areas = useMemo(() => {
        const iashMap = {};
        (iashTotals?.average_by_management_area || []).forEach(a => { iashMap[a.name] = a.average; });
        const ipraMap = {};
        (ipraTotals?.average_by_management_area || []).forEach(a => { ipraMap[a.name] = a.average; });
        const names = new Set([...Object.keys(iashMap), ...Object.keys(ipraMap)]);
        return [...names].map(name => {
            const iash   = iashMap[name] ?? null;
            const ipra   = ipraMap[name] ?? null;
            const icpRaw = iash != null && ipra != null ? (iash + ipra) / 2 : (iash ?? ipra);
            return { name, iash, ipra, icp: icpRaw != null ? Math.round(icpRaw) : null, ivcLevel: computeIvcLevel(ipra, iash) };
        });
    }, [iashTotals, ipraTotals]);

    const iashDimRows = useMemo(
        () => toDimensionRows(iashTotals?.average_by_dimension).filter(r => r.average != null),
        [iashTotals]
    );
    const ipraDimRows = useMemo(
        () => toDimensionRows(ipraTotals?.average_by_dimension).filter(r => r.average != null),
        [ipraTotals]
    );

    // Build section data
    const criticalFindings = useMemo(
        () => buildCriticalFindings(areas, criticalSups, headcountMap, ipraTotals),
        [areas, criticalSups, headcountMap, ipraTotals]
    );
    const moderateFindings = useMemo(
        () => buildModerateFindings(iashDimRows, ipraDimRows, kpis?.ipra_nps),
        [iashDimRows, ipraDimRows, kpis]
    );
    const lowFindings = useMemo(
        () => buildLowFindings(iashDimRows, ipraDimRows),
        [iashDimRows, ipraDimRows]
    );

    // Assign sections in order, skip empty, compute sequential hallazgo numbering
    const sections = [];
    if (criticalFindings.length > 0) sections.push({ tierKey: 'critical', sectionTitle: null,                    findings: criticalFindings });
    if (moderateFindings.length > 0) sections.push({ tierKey: 'moderate', sectionTitle: 'Indicadores de Co-falla', findings: moderateFindings });
    if (lowFindings.length > 0)      sections.push({ tierKey: 'low',      sectionTitle: 'Activos de Seguridad',   findings: lowFindings });

    // Sequential start indexes (H01, H02…)
    let counter = 1;
    const indexedSections = sections.map(s => {
        const start = counter;
        counter += s.findings.length;
        return { ...s, startIndex: start };
    });

    return (
        <div className="mgcr-kf-page">

            {/* ── Page header ── */}
            <div className="mgcr-page-header">
                <span className="mgcr-page-header-text">{headerText}</span>
                <span className="mgcr-page-header-num">{pageNumber}</span>
            </div>

            {/* ── Titles ── */}
            <div className="mgcr-kf-title-area">
                <h2 className="mgcr-kf-main-title">Hallazgos Clave</h2>
                <p  className="mgcr-kf-subtitle">Factores de Decisión</p>
            </div>

            {/* ── Sections ── */}
            <div className="mgcr-kf-body">
                {indexedSections.map((s, i) => (
                    <FindingsSection
                        key={s.tierKey}
                        tierKey={s.tierKey}
                        sectionTitle={s.sectionTitle}
                        findings={s.findings}
                        startIndex={s.startIndex}
                    />
                ))}
            </div>

            <ManCrossPageFooter />
        </div>
    );
};

export default ManCrossPageKeyFindings;
